import orderBy from 'lodash/orderBy';

import { Optional } from 'chaika-common-types';
import { ReferralStatus } from 'chaika-enums';
import { OutwardReferralData, ReferralData } from 'chaika-frontend-types/frontend-cms';

import type { GroupedListParams } from '@store/abstract/GroupedList';
import type { ReferralField } from '@store/models/EntryFields';

import { GroupedItems, ItemParams, ItemType, TreatmentPlanItems } from '@common/TreatmentPlan/types';
import { isOutwardReferralOfEntry } from '@common/TreatmentPlan/utils/outwardReferralFilter';
import {
    drugToItemParams,
    entryToItemParams,
    outwardReferralToItemParams,
    recommendationToItemParams,
    referralToItemParams,
    reservationToItemParams,
    taskToItemParams,
} from '@common/TreatmentPlan/utils/treatmentPlanData';

export interface SplittedReferrals {
    referrals: GroupedListParams<ReferralData>[];
    allReferrals: GroupedListParams<ReferralData>[];
    referralsWithReservations: GroupedListParams<ReferralData>[];
}

interface GroupItemsParams {
    fromEhr: boolean;
    isOnPrint: boolean;
    entryDate?: string;
}

interface SplitReferralsParams {
    entryId: string;
    isOnPrint: boolean;
}

export function makeGroupedItems(
    { reservations, tasks, entries, referrals, drugs, recommendations, outwardReferrals, drugList }: TreatmentPlanItems,
    { fromEhr, isOnPrint, entryDate }: GroupItemsParams,
): GroupedItems {
    const items: (ItemParams & { orderByDate: string })[] = [
        ...reservations.map(reservation => reservationToItemParams(reservation)),
        ...tasks.map(task => taskToItemParams(task)),
        ...entries.map(entry => entryToItemParams(entry)),
        ...referrals.map(referral => referralToItemParams(referral, fromEhr)),
        ...drugs.map(id => drugToItemParams(drugList.getExisting(id), !fromEhr ? entryDate : undefined)),
        ...recommendations.map(recommendation => recommendationToItemParams(recommendation)),
        ...outwardReferrals.map(outwardReferral => outwardReferralToItemParams(outwardReferral)),
    ];

    const filteredItems = isOnPrint ? filterPrintableItems(items) : items;
    const orderedItems = orderBy(filteredItems, 'orderByDate');

    return {
        currentItemId: findCurrentItem(orderedItems, filteredItems)?.id,
        items: orderedItems,
    };
}

export function splitReferralsByReservation(
    referralField: ReferralField,
    referralIds: string[],
    { entryId, isOnPrint }: SplitReferralsParams,
): SplittedReferrals {
    const allReferrals = referralField.getValue().getFilteredValues({
        ids: referralIds,
        entryId: isOnPrint ? entryId : undefined,
    });

    const active = allReferrals.filter(({ status }) => status !== ReferralStatus.Cancelled);

    return {
        allReferrals,
        referrals: active.filter(({ reservationExist }) => !reservationExist),
        referralsWithReservations: active.filter(({ reservationExist }) => reservationExist),
    };
}

export function filterEntryOutwardReferrals(
    outwardReferrals: OutwardReferralData[],
    entryId: string,
): OutwardReferralData[] {
    return outwardReferrals.filter(outwardReferral => isOutwardReferralOfEntry(outwardReferral, entryId));
}

function findCurrentItem(
    orderedItems: (ItemParams & { orderByDate: string })[],
    filteredItems: (ItemParams & { orderByDate: string })[],
): Optional<ItemParams> {
    return (
        orderedItems.find(({ isExpired, endDate }) => !isExpired && endDate) ?? filteredItems[filteredItems.length - 1]
    );
}

function filterPrintableItems(
    items: (ItemParams & { orderByDate: string })[],
): (ItemParams & { orderByDate: string })[] {
    return items.filter(({ isExpired, isExecuted, hideOnPrint, type }) =>
        type === ItemType.Referral || type === ItemType.Recommendation
            ? !hideOnPrint
            : !isExpired && !isExecuted && !hideOnPrint,
    );
}
